import {
  type ComponentType,
  type PropsWithChildren,
  useCallback,
  useRef,
  useState,
} from 'react';

import { lruMemoize } from 'reselect';

import { useAppSelector } from '../../store/react_redux_typed';
import type { ObjectWithId } from '../../types/common';
import type { MenuItem } from '../../ui/menus/menu/types';
import PopoverTrigger, {
  type PopoverTriggerHandle,
} from '../../ui/popover_trigger/popover_trigger';
import SelectableItemsUI, {
  type Props as UIComponentProps,
} from '../../ui/selectable_items/selectable_items';
import type {
  ContextMenuContainerProps,
  MenuItemEvent,
} from '../context_menu/types';

type Props<Item extends ObjectWithId, AppState, Options> = PropsWithChildren<{
  contextMenu?: ComponentType<
    ContextMenuContainerProps<Item, AppState, Options>
  >;
  contextMenuOptions?: Options;
  onContextMenuItemClicked?: (menuItem: MenuItem, event: MenuItemEvent) => void;
  onMenuClose?: () => void;
  idKey?: string;
}> &
  Omit<
    UIComponentProps<Item>,
    'lastSelectedItemId' | 'isSelectionLocked' | 'onContextMenu'
  >;

const getSelectedItems = lruMemoize(
  <Item extends ObjectWithId>(items: Item[], ids: Set<string>, idKey = 'id') =>
    items.filter((item) => ids.has(item[idKey])),
);

const SelectableItems = <Item extends ObjectWithId, AppState, Options>(
  props: Props<Item, AppState, Options>,
) => {
  const popoverTriggerHandle = useRef<PopoverTriggerHandle>(null);
  const [selectedIds, setSelectedIds] = useState(new Set<string>());
  const isSelectionLocked = useAppSelector((state) =>
    Object.values(state.modalsManager).some((modal) => modal.isOpen),
  );

  const {
    children,
    contextMenu: CustomContextMenuComponent,
    contextMenuOptions,
    onContextMenuItemClicked,
  } = props;

  const selectedItems = getSelectedItems(props.items, selectedIds, props.idKey);

  const handleMenuItemClick = useCallback(
    (menuItem: MenuItem, event: MenuItemEvent) => {
      if (menuItem.autoClose !== false && !event?.defaultPrevented) {
        popoverTriggerHandle.current?.close();
      }
      onContextMenuItemClicked?.(menuItem, event);
    },
    [onContextMenuItemClicked],
  );

  const handleAdditionalMenuItemClick = useCallback(
    (menuItem: MenuItem, event: MenuItemEvent) => {
      if (
        menuItem.additionalMenuItem?.autoClose !== false &&
        !event?.defaultPrevented
      ) {
        popoverTriggerHandle.current?.close();
      }
    },
    [],
  );

  return (
    <PopoverTrigger
      triggerElement={
        <SelectableItemsUI
          {...props}
          isSelectionLocked={isSelectionLocked}
          onSelectIds={(ids) => {
            if (ids.length === 0) {
              return;
            }
            const newIds = new Set(ids);
            setSelectedIds(newIds);
            props.onSelectIds?.(ids);
          }}
        >
          {children}
        </SelectableItemsUI>
      }
      triggerOn="contextmenu"
      ref={popoverTriggerHandle}
      onClose={props.onMenuClose}
    >
      {CustomContextMenuComponent ? (
        <CustomContextMenuComponent
          contextItems={selectedItems}
          options={contextMenuOptions as Options}
          onMenuItemClick={handleMenuItemClick}
          onAdditionalMenuItemClick={handleAdditionalMenuItemClick}
        />
      ) : null}
    </PopoverTrigger>
  );
};

export default SelectableItems;
